<template>
  <div class="segment-result-list">
    <div class="list-header">
      <div class="list-header__left">
        <el-checkbox
          :value="allSelected"
          :indeterminate="indeterminate"
          @change="toggleAll"
        >
          全选
        </el-checkbox>
        <h3>{{ listTitle }}（{{ visibleClips.length }} 段）</h3>
      </div>
    </div>
    <p v-if="hint" class="list-hint">{{ hint }}</p>
    <p v-if="countNote" class="list-count-note">{{ countNote }}</p>
    <div class="list-toolbar">
      <el-button
        size="small"
        type="primary"
        :disabled="!selectedClips.length"
        @click="$emit('batch-download', selectedClips)"
      >
        批量下载 ({{ selectedClips.length }})
      </el-button>
    </div>
    <div class="cards" :class="{ 'cards--cover': hasCoverMode }">
      <segment-result-card
        v-for="clip in visibleClips"
        :key="clipKey(clip)"
        :clip="clip"
        :selected="isSelected(clip)"
        :tuneable="tuneable"
        :feedbackable="feedbackable"
        :live-category="liveCategory"
        :source-duration-seconds="sourceDurationSeconds"
        :refining="refiningId === clip.id"
        @play="$emit('play', clip)"
        @download="$emit('download-one', clip)"
        @toggle-select="toggleOne"
        @unavailable="onClipUnavailable"
        @refine="$emit('refine', $event)"
        @feedback="$emit('feedback', $event)"
      />
    </div>
    <el-dialog
      v-if="playingClip"
      :visible.sync="dialogVisible"
      width="720px"
      :title="playingClip.fileName"
      @close="$emit('close-player')"
    >
      <video
        v-if="playingClip && playingClip.url"
        class="player"
        :src="playingClip.url"
        controls
        autoplay
        @error="onPlayerError"
      />
    </el-dialog>
  </div>
</template>

<script>
import SegmentResultCard from './SegmentResultCard.vue';

export default {
  name: 'SegmentResultList',
  components: { SegmentResultCard },
  props: {
    clips: { type: Array, default: () => [] },
    playingClip: { type: Object, default: null },
    title: { type: String, default: '切片结果' },
    tuneable: { type: Boolean, default: false },
    feedbackable: { type: Boolean, default: false },
    liveCategory: { type: String, default: 'GAME' },
    hint: { type: String, default: '' },
    requestedCount: { type: Number, default: 0 },
    sourceDurationSeconds: { type: Number, default: 0 },
    refiningId: { type: String, default: '' },
  },
  data() {
    return {
      selectedKeys: [],
      unavailableKeys: [],
    };
  },
  computed: {
    listTitle() {
      return this.title || '切片结果';
    },
    countNote() {
      const requested = Number(this.requestedCount) || 0;
      const actual = this.visibleClips.length;
      if (requested <= 0 || actual >= requested) {
        return '';
      }
      const prefix = `已选出 ${actual} 段（目标 ${requested} 条）。`;
      const cat = String(this.liveCategory || 'GAME').toUpperCase();
      if (cat === 'SING' || cat === 'DANCE') {
        return `${prefix}不足时通常因为未达整段时长门槛（宁缺毋滥），属正常现象。`;
      }
      if (cat === 'SHOW') {
        return `${prefix}不足时通常因为互动热区重合或片段过短被合并/过滤，属正常现象。`;
      }
      return `${prefix}不足时通常因为高燃区间重合、片段过短或质量偏低被合并/过滤，属正常现象。`;
    },
    visibleClips() {
      return this.clips.filter(clip => !this.unavailableKeys.includes(this.clipKey(clip)));
    },
    dialogVisible: {
      get() {
        return !!this.playingClip;
      },
      set(val) {
        if (!val) {
          this.$emit('close-player');
        }
      },
    },
    allSelected() {
      return this.visibleClips.length > 0
        && this.visibleClips.every(clip => this.selectedKeys.includes(this.clipKey(clip)));
    },
    indeterminate() {
      return this.selectedKeys.length > 0 && !this.allSelected;
    },
    selectedClips() {
      return this.visibleClips.filter(clip => this.selectedKeys.includes(this.clipKey(clip)));
    },
    hasCoverMode() {
      return this.visibleClips.some(clip => clip.coverEnabled);
    },
  },
  watch: {
    clips() {
      this.selectedKeys = [];
      this.unavailableKeys = [];
      this.emitSelection();
    },
    selectedKeys() {
      this.emitSelection();
    },
  },
  methods: {
    clipKey(clip) {
      return String(clip.id != null ? clip.id : clip.index);
    },
    isSelected(clip) {
      return this.selectedKeys.includes(this.clipKey(clip));
    },
    toggleAll(checked) {
      if (checked) {
        this.selectedKeys = this.visibleClips.map(clip => this.clipKey(clip));
      } else {
        this.selectedKeys = [];
      }
    },
    toggleOne(clip, checked) {
      const key = this.clipKey(clip);
      if (checked) {
        if (!this.selectedKeys.includes(key)) {
          this.selectedKeys = [...this.selectedKeys, key];
        }
      } else {
        this.selectedKeys = this.selectedKeys.filter(item => item !== key);
      }
    },
    emitSelection() {
      this.$emit('selection-change', this.selectedClips);
    },
    onClipUnavailable(clip) {
      const key = this.clipKey(clip);
      if (this.unavailableKeys.includes(key)) {
        return;
      }
      this.unavailableKeys = [...this.unavailableKeys, key];
      this.selectedKeys = this.selectedKeys.filter(item => item !== key);
      this.$emit('remove-clip', clip);
    },
    onPlayerError() {
      if (this.playingClip) {
        this.onClipUnavailable(this.playingClip);
        this.$emit('close-player');
      }
    },
  },
};
</script>

<style scoped>
.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.list-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.list-header__left h3 {
  margin: 0;
}
.list-hint {
  margin: 0 0 6px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}
.list-count-note {
  margin: 0 0 10px;
  font-size: 13px;
  color: #b45309;
  line-height: 1.5;
}
.list-toolbar {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 16px;
}
.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  align-items: stretch;
}
.cards--cover {
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}
.cards >>> .segment-result-card {
  height: 100%;
}
.player {
  width: 100%;
  max-height: 420px;
  background: #000;
}
</style>
